import { RestrictedRoute } from "RestrictedRoute";
import { QuickNavModal } from "components/QuickNavModal/QuickNavModal";
import { TAX_QUICK_NAV_PAGES } from "constants/quickNav/tax";
import Loader from "components/DesignSystem/Loader/loader";
import { AppLayout } from "components/AppLayout/AppLayout";
import { Entities } from "components/Entity/Entity";
import { RoutesAccrossApps } from "components/RoutesAcrossApps/RoutesAcrossApps";
import { LeftBar, LeftBarItem, LeftBarLogo } from "components/Sidebar/LeftBar";
import { SidebarStickyGroup } from "components/Sidebar/SidebarStickyGroup";
import { TaskList } from "components/Task/TaskList";
import { TopBar } from "components/TopBar/TopBar";
import GroupSelect from "components/dashboard/sideBar/groupSelect";
import { Home as TaxHomePage } from "components/home/Home";
import { Filings as FilingsIcon } from "components/icons/LeftNav/Filings";
import { Home } from "components/icons/LeftNav/Home";
import { MyEntities } from "components/icons/LeftNav/MyEntities";
import { IncorporationIcon } from "components/icons/IncorporationIcon";
import { RegistrationsIcon } from "components/icons/LeftNav/RegistrationsIcon";
import { InkleTaxLogo } from "components/icons/Logos/InkleTaxLogo";
import { InkleTaxSymbol } from "components/icons/Logos/InkleTaxSymbol";
import AddTaskEntityModal from "components/tasks/modal/AddTaskEntityModal";
import TemplateMenu from "components/tasks/modal/AllTemplatesModal";
import { ForeignCATask } from "components/tasks/taskForForeignCA/ForeignCATask";
import { TaxAndCompliance } from "components/taxCompliance/TaxAndCompliance";
import { FOREIGN_CA } from "constants/onBoarding";
import { useAuth } from "hooks/useAuth";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import authContext from "jwt_context&axios/authContext";
import { AdminNavbar } from "pages/AdminDashboard/AdminDashboard";
import { PracticeNavbar } from "pages/PracticeDashboard";
import { Registrations } from "pages/Registrations/Registrations";
import { StateRegistrationDetail } from "pages/Registrations/StateRegistrationDetail";
import { useContext, useEffect } from "react";
import { Redirect, Switch, useLocation, useRouteMatch } from "react-router-dom";
import { DashboardBanner } from "signInSignUp/DashboardBanner";
import "static/styles/containers/dashboard.css";
import { useCurrentAppContext } from "contexts/CurrentAppContext";
import { RAndDIcon } from "components/icons/LeftNav/RAndDIcon";
import { RAndD } from "pages/RAndD/RAndD";
import { SubMenu } from "components/Sidebar/SubMenu";
import { parse, stringify } from "qs";
import { AddressBook } from "./AddressBook";
import { IncorporationRoute } from "pages/Incorporation/IncorporationRoute";
import { AdvanceTaxAndProfitsIcon } from "components/icons/LeftNav/AdvanceTaxAndProfits";
import { AdvanceTaxAndProfitsDashboard } from "components/AdvanceTaxAndProfits/AdvanceTaxAndProfitsDashboard";
import { StateIncomeTax } from "components/StateIncomeTax/StateIncomeTax";
import { useDashboardBanner } from "hooks/useDashboardBanner";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { FilingsDashboard } from "v2/pages/FilingsDashboard/FilingsDashboard";
import { useIsGroupV2Platform } from "hooks/useIsGroupV2Platform";

export const Navbar = () => {
  const { type } = useAuth();
  const location = useLocation();
  const parsedSearch = parse(location.search, { ignoreQueryPrefix: true });

  const { entities, is_pre_incorporate } = useCurrentGroupContext();
  const isV2Platform = useIsGroupV2Platform();

  const entityIdMatch = location.pathname.match(/\/entities\/entity\/([^/]+)/);
  const activeEntityId = entityIdMatch?.[1] ?? entities[0]?.uuid;
  const entityBase = `/entities/entity/${activeEntityId}`;

  const search = stringify(
    {
      entity: parsedSearch.entity,
      company: parsedSearch.company,
    },
    { skipNulls: true, addQueryPrefix: true }
  );

  return (
    <div className="t-h-full t-flex t-flex-col t-justify-between">
      <div>
        {type === FOREIGN_CA && (
          <div className="t-m-2">
            <GroupSelect />
          </div>
        )}
        <LeftBarItem exact to="/" icon={Home}>
          Home
        </LeftBarItem>
        <LeftBarItem to="/filings" icon={FilingsIcon}>
          Filings
        </LeftBarItem>
        <SubMenu.Root
          title="Estimated Tax"
          defaultRoute={`/advance-tax${search}`}
          childPaths={["/advance-tax"]}
        >
          <SubMenu.Trigger icon={AdvanceTaxAndProfitsIcon}>
            Estimated Tax
          </SubMenu.Trigger>
          <SubMenu.Container hoverTitle="Estimated Tax">
            <SubMenu.Item to={`/advance-tax${search}`} useParentPath exact>
              Overview
            </SubMenu.Item>
            <SubMenu.Item to={`/advance-tax/breakdown${search}`} useParentPath>
              Monthly Breakdown
            </SubMenu.Item>
            <SubMenu.Item
              to={`/advance-tax/tax-benefits${search}`}
              useParentPath
            >
              Tax benefits
            </SubMenu.Item>
            <SubMenu.Item to={`/advance-tax/settings${search}`} useParentPath>
              Settings
            </SubMenu.Item>
          </SubMenu.Container>
        </SubMenu.Root>
        <LeftBarItem to="/state-compliance" icon={RegistrationsIcon}>
          State Compliance
        </LeftBarItem>

        <LeftBarItem to="/r-and-d" icon={RAndDIcon}>
          R&D Tax Credits
        </LeftBarItem>
        {is_pre_incorporate && (
          <LeftBarItem to="/incorporation" icon={IncorporationIcon}>
            Incorporation
          </LeftBarItem>
        )}
        <SubMenu.Root
          title="My Entities"
          defaultRoute="/entities"
          childPaths={["/entities", "/v2/entity", "/address-book"]}
        >
          <SubMenu.Trigger icon={MyEntities}>My Entities</SubMenu.Trigger>
          <SubMenu.Container hoverTitle="My Entities">
            <SubMenu.Item to="/entities" useParentPath exact>
              All Entities
            </SubMenu.Item>

            {activeEntityId && (
              <>
                {isV2Platform && (
                  <SubMenu.Item to={entityBase} useParentPath exact>
                    Records & Setup
                  </SubMenu.Item>
                )}

                <SubMenu.Item
                  to={isV2Platform ? `${entityBase}/profile` : entityBase}
                  useParentPath
                  exact
                >
                  Entity Details
                </SubMenu.Item>

                <SubMenu.Item
                  to={`${entityBase}/people-ownership`}
                  useParentPath
                  exact
                >
                  People & ownership
                </SubMenu.Item>

                <SubMenu.Item
                  to={`${entityBase}/sensitive-data`}
                  useParentPath
                  exact
                >
                  Sensitive Data
                </SubMenu.Item>
              </>
            )}

            <SubMenu.Item to="/address-book" useParentPath>
              Address Book
            </SubMenu.Item>
          </SubMenu.Container>
        </SubMenu.Root>
      </div>
    </div>
  );
};

export const TaxDashboard = () => {
  const { isCustomer, isForeignCA } = useRoleBasedView();

  const { path, url } = useRouteMatch();
  const { authtoken } = useContext(authContext);
  const { shouldRenderBanner } = useDashboardBanner();

  const { uuid, has_paid_for_tax_filing } = useCurrentGroupContext();
  const isV2Platform = useIsGroupV2Platform();

  const isGroupLoaded = Boolean(uuid);
  const showFilingsDashboard = isV2Platform && !has_paid_for_tax_filing;

  const renderTaxHome = () => {
    if (!isGroupLoaded) return <Loader />;
    if (showFilingsDashboard) return <FilingsDashboard />;
    return <TaxHomePage />;
  };

  const Nav = isCustomer ? Navbar : isForeignCA ? PracticeNavbar : AdminNavbar;

  const baseLink = isCustomer ? url : isForeignCA ? "/practice" : "/admin";

  const { dispatch: currentAppDispatch } = useCurrentAppContext();
  const location = useLocation();

  const parsedSearch = parse(location.search, { ignoreQueryPrefix: true });

  const search = stringify(
    {
      entity: parsedSearch.entity,
      company: parsedSearch.company,
    },
    { skipNulls: true, addQueryPrefix: true }
  );

  useEffect(() => {
    currentAppDispatch({
      type: "SET_CURRENT_APP",
      payload: { currentApp: "Tax" },
    });
  }, []);

  return (
    <>
      <AppLayout
        sidebar={
          <LeftBar
            logo={
              <LeftBarLogo
                url={baseLink}
                logo={InkleTaxLogo}
                symbol={InkleTaxSymbol}
              />
            }
            showCollapse
            stickyNav={<SidebarStickyGroup />}
          >
            {<Nav />}
          </LeftBar>
        }
        topbar={
          <TopBar
            logo={<LeftBarLogo logo={InkleTaxLogo} symbol={InkleTaxSymbol} />}
            nav={<Nav />}
            stickyNav={<SidebarStickyGroup />}
          />
        }
        banner={<DashboardBanner />}
        noVerticalGap={Boolean(!shouldRenderBanner)}
      >
        <QuickNavModal pages={TAX_QUICK_NAV_PAGES} />
        <Switch>
          <RestrictedRoute
            exact
            path={`${path}/tax`}
            component={TaxAndCompliance}
          />
          <RestrictedRoute
            exact
            path={`${path}/state-compliance`}
            component={Registrations}
          />
          <RestrictedRoute
            exact
            path={`${path}/state-compliance/:stateId`}
            component={StateRegistrationDetail}
          />
          <RestrictedRoute
            path={`${path}/advance-tax`}
            component={AdvanceTaxAndProfitsDashboard}
          />
          {!isCustomer && (
            <RestrictedRoute
              path={`${path}/state-income-tax`}
              component={StateIncomeTax}
            />
          )}
          <RestrictedRoute
            exact
            path={`${path}/filings/addtask`}
            render={() => <TemplateMenu to="/filings" />}
          />
          <RestrictedRoute
            exact
            path={`${path}/filings/addtask/:templateId`}
            render={() => <AddTaskEntityModal to="/filings" />}
          />
          <RestrictedRoute exact path={`${path}/tasks/addtask`}>
            <Redirect to={`${path}/filings/addtask`} />
          </RestrictedRoute>

          <RestrictedRoute
            exact
            path={`${path}/tasks/:taskId`}
            render={({
              match: {
                params: { taskId },
              },
            }) => <Redirect to={`${path}/filings/${taskId}`} />}
          />
          <RestrictedRoute exact path={`${path}/r-and-d`} component={RAndD} />
          <RestrictedRoute
            exact
            path={`${path}/incorporation`}
            component={IncorporationRoute}
          />
          <Redirect exact from={`${path}/others`} to={`${path}/filings`} />
          <Redirect
            exact
            from={`${path}/others/dissolution`}
            to={`${path}/filings`}
          />
          <RestrictedRoute
            exact
            path={`${path}/tasks/addtask/:templateId`}
            render={({
              match: {
                params: { templateId },
              },
            }) => <Redirect to={`/filings/addtask/${templateId}`} />}
          />
          <RestrictedRoute
            path={`${path}/filings`}
            component={
              authtoken?.is_any_service_user ? ForeignCATask : TaskList
            }
          />
          <RestrictedRoute exact path="/tasks">
            <Redirect to={`${path}/filings`} />
          </RestrictedRoute>
          <Redirect
            from={`${path}/entities/autofill`}
            to={`${path}/address-book`}
          />
          <Redirect
            from={`${path}/entities/autofill/bank_accounts`}
            to={`${path}/address-book/bank_accounts${search}`}
          />
          <Redirect
            from={`${path}/entities/autofill/authorized_persons`}
            to={`${path}/address-book/authorized_persons${search}`}
          />
          <Redirect
            from={`${path}/entities/autofill/addresses`}
            to={`${path}/address-book/addresses${search}`}
          />

          <RestrictedRoute path={`${path}/entities`} component={Entities} />
          <RestrictedRoute
            path={`${path}/address-book`}
            component={AddressBook}
          />
          <RestrictedRoute
            exact
            path={`${path}/filings-dashboard`}
            component={FilingsDashboard}
          />
          <RestrictedRoute exact path={path} render={renderTaxHome} />
          <RoutesAccrossApps />
        </Switch>
      </AppLayout>
    </>
  );
};
